'use client';

import Image from 'next/image';
import {useRouter, usePathname} from 'next/navigation';

import skillzLogo from '@/../public/skillzLogo.svg';
import skillzWordmark from '@/../public/skillz_wordmark_white.svg';
import backArrow from '@/../public/icon_back_arrow_white.svg';

import {trackArcadeEvent, ARCADE_EVENT} from '@/app/(app)/_src/components/WebArcadeLobby/trackArcadeEvent';
import {shouldShowWebArcadeHeader} from '@/utils/shouldShowWebArcadeHeader';
import styles from './WebArcadeReturnHeader.styles';

// ?ref=arcade_header lets the lobby distinguish header-driven visits from direct ones.
const ARCADE_LOBBY_PATH = '/?ref=arcade_header';

export interface WebArcadeReturnHeaderPartner {
  /** Partner slug, for analytics. */
  slug: string;
  /** Return-bar label, e.g. "Back to Games" (Lycos) or "Back to waitup.com". */
  backText: string;
  /** Relative path on our host, or an absolute URL to the partner's own site. */
  backHref: string;
}

interface WebArcadeReturnHeaderProps {
  /**
   * When present, the bar is co-branded for a partner: the back label/target come
   * from the tenant, not the default Skillz Arcade. Absolute `backHref`s (e.g. the
   * partner's own site) navigate the top window; relative ones use the router.
   */
  partner?: WebArcadeReturnHeaderPartner;
}

/**
 * Persistent return header for the game lobby. Default: "Back to Skillz Arcade".
 * Co-branded per partner when a `partner` prop is supplied.
 */
export default function WebArcadeReturnHeader({partner}: WebArcadeReturnHeaderProps = {}) {
  const router = useRouter();
  const pathname = usePathname();

  if (!shouldShowWebArcadeHeader(pathname)) return null;

  const backText = partner?.backText ?? 'Back to Skillz Arcade';
  const backHref = partner?.backHref ?? ARCADE_LOBBY_PATH;
  const isExternal = /^https?:\/\//i.test(backHref);

  const navigateBack = () => {
    // Extract gameId from the current path: /games/{gid}/...
    const pathParts = pathname.split('/');
    const gamesIndex = pathParts.indexOf('games');
    const fromGameId = gamesIndex >= 0 ? Number(pathParts[gamesIndex + 1]) || 0 : 0;

    trackArcadeEvent(ARCADE_EVENT.RETURN_CLICKED, {
      result: JSON.stringify({
        from_game_id: fromGameId,
        from_path: pathname,
        ...(partner ? {partner: partner.slug} : {}),
      }),
    });

    if (isExternal) {
      window.location.href = backHref;
    } else {
      router.push(backHref);
    }
  };

  return (
    <header className={styles.rootClx}>
      <button
        type='button'
        aria-label={partner ? `${partner.slug} home` : 'Skillz Arcade home'}
        className={styles.brandCtaClx}
        onClick={navigateBack}
      >
        <Image src={skillzLogo} alt='Skillz' className={styles.logoClx} priority />
        <Image src={skillzWordmark} alt='' aria-hidden className={styles.wordmarkClx} priority />
      </button>
      <button type='button' className={styles.backCtaClx} onClick={navigateBack}>
        <Image src={backArrow} alt='' aria-hidden className={styles.backArrowClx} />
        <span className={styles.backTextClx}>{backText}</span>
      </button>
    </header>
  );
}
